export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Driving Foundations, Vehicle Control & First Lessons',
    subtitle: 'Build the foundation every new driver needs.',
    intro: [
      'A new driver needs more than information about the controls.',
      'They need a clear sequence that takes them from their first moments behind the wheel toward real control and confidence.',
      'This volume gives the instructor a structured way to teach the fundamentals instead of rebuilding every beginner lesson.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Cockpit setup',
      'Vehicle controls',
      'Moving off',
      'Stopping',
      'Steering',
      'Braking',
      'Acceleration',
      'Observation routines',
      'Basic mirror use',
      'First road positioning',
      'Beginner errors',
      'Instructor cues',
      'Foundational progressions',
      'First-lesson structures',
    ],
    result:
      'Your student develops stronger foundations while you follow a clear and repeatable teaching progression.',
  },
  {
    number: 'Volume 2',
    title: 'Observation, Road Positioning & Traffic Awareness',
    subtitle: 'Teach students to see, anticipate and position correctly.',
    intro: ['Vehicle control alone is not enough.', 'Students need to understand:'],
    questions: [
      'Where should I look?',
      'What should I notice?',
      'Where should the vehicle be?',
      'What might happen next?',
    ],
    bulletsIntro: 'This volume helps you teach:',
    bullets: [
      'Mirrors',
      'Blind spots',
      'Scanning',
      'Following distance',
      'Lane positioning',
      'Road positioning',
      'Pedestrians',
      'Cyclists',
      'Parked vehicles',
      'Road markings',
      'Anticipation',
      'Hazard recognition',
      'Observation routines',
      'Error correction',
    ],
    result:
      'Students begin making better decisions because they understand what to observe and how to position the vehicle.',
  },
  {
    number: 'Volume 3',
    title: 'Junctions, Intersections, Roundabouts & Lane Decisions',
    subtitle: 'Turn complex road situations into clear visual teaching sequences.',
    intro: [
      'Junctions and roundabouts can quickly overwhelm developing drivers.',
      'Instead of explaining everything verbally, this volume helps you break the situation down visually.',
    ],
    bulletsIntro: 'Includes:',
    bullets: [
      'Junction approach',
      'Intersections',
      'Turning',
      'T-junctions',
      'Crossroads',
      'Roundabouts',
      'Lane selection',
      'Merging',
      'Lane changes',
      'Gap awareness',
      'Observation',
      'Positioning',
      'Decision-making',
      'Correct-vs-incorrect examples',
      'Instructor corrections',
    ],
    result: 'Students can see what should happen before they attempt the situation on the road.',
  },
  {
    number: 'Volume 4',
    title: 'Parking, Reversing & Essential Maneuvers',
    subtitle: 'Visual step-by-step instruction for the maneuvers students struggle with most.',
    intro: [],
    bulletsIntro: 'Includes:',
    bullets: [
      'Parallel parking',
      'Forward bay parking',
      'Reverse bay parking',
      'Angle parking',
      'Straight reversing',
      'Confined-space maneuvering',
      'Hill starts',
      'Pulling up',
      'Moving off',
      'Kerb awareness',
      'Front vehicle path',
      'Rear wheel path',
      'Steering timing',
      'Visual reference points',
      'Common parking mistakes',
      'Quick corrections',
    ],
    sequenceIntro: 'The material uses:',
    sequence: 'Start position → Movement → Steering → Correction → Final position',
    result:
      'Instead of explaining the same maneuver repeatedly, you can show the student exactly how the vehicle should move.',
  },
  {
    number: 'Volume 5',
    title: 'Hazard Awareness, Complex Roads & Independent Driving',
    subtitle: 'Help students progress from vehicle control to independent decision-making.',
    intro: [],
    bulletsIntro: 'Includes:',
    bullets: [
      'Urban traffic',
      'Busier roads',
      'Higher-speed roads',
      'Merging',
      'Lane discipline',
      'Hazard awareness',
      'Anticipation',
      'Independent driving',
      'Planning ahead',
      'Changing traffic situations',
      'Reduced visibility',
      'Weather considerations',
      'Decision-making',
      'Instructor prompt reduction',
    ],
    result: 'Students learn to observe, plan and make increasingly independent driving decisions.',
  },
  {
    number: 'Volume 6',
    title: '150 Ready-to-Teach Driving Lessons — Beginner to Test-Ready',
    subtitle: 'Stop building every driving lesson from scratch.',
    intro: ['This is the practical heart of the complete system.', 'You receive:'],
    highlight: '150 COMPLETE READY-TO-TEACH DRIVING LESSONS',
    afterHighlight: 'organized so you can open the system, choose the objective and begin teaching.',
    bulletsIntro: 'Lesson areas include:',
    bullets: [
      'Vehicle control',
      'First lessons',
      'Steering',
      'Braking',
      'Observation',
      'Mirrors',
      'Positioning',
      'Junctions',
      'Intersections',
      'Roundabouts',
      'Lane changes',
      'Parking',
      'Reversing',
      'Hazards',
      'Complex roads',
      'Independent driving',
      'Assessment',
      'Test preparation',
    ],
    secondaryBulletsIntro: 'Lessons can include:',
    secondaryBullets: [
      'Objective',
      'Student level',
      'Setup',
      'Visual explanation',
      'Instructor demonstration',
      'Student practice',
      'What to observe',
      'Instructor cues',
      'Common errors',
      'Correction',
      'Easier version',
      'Progression',
      'Success check',
    ],
    resultLead: 'Instead of asking:',
    resultQuote: '“What am I going to teach this student today?”',
    result: 'you open the system and already have the lesson.',
    popular: true,
  },
]
